import {navigator as turboNavigator} from "@hotwired/turbo";
import {removeEmpty} from "./object.js";

// Fired on the form when its data-turbo-confirm dialog is declined or dismissed. Turbo reports
// no event for that answer.
export const CONFIRM_DECLINED_EVENT = "confirm:declined";

// The part of Turbo's page navigator and its pending form submission that the answer path uses.
interface PendingSubmission {
   formElement: HTMLFormElement;
   state: string;
}

interface SubmittingNavigator {
   formSubmission?: PendingSubmission;
   submitForm(formElement: HTMLFormElement, submitter?: HTMLElement): void;
}

const pageNavigator = turboNavigator as unknown as SubmittingNavigator;

// The form whose confirm was already answered: the re-submit below must not ask again.
let answeredForm: HTMLFormElement | null = null;

export class Confirm {

   static turboConfirmation(message, formElement, submitter) {
      if (formElement === answeredForm) {
         return Promise.resolve(true);
      }

      // A page refresh starts a visit, and a visit stops the pending submission (state "initialized")
      // that is waiting on this dialog. The dialog survives the refresh, so its answer must still act.
      const pending = pageNavigator.formSubmission?.formElement === formElement ? pageNavigator.formSubmission : null;

      const dialogId = formElement.getAttribute("data-confirm-dialog-id");
      const title = formElement.getAttribute("data-confirm-dialog-title");
      const cancel = formElement.getAttribute("data-confirm-dialog-cancel");
      const confirm = formElement.getAttribute("data-confirm-dialog-confirm");

      return Confirm.askConfirmation({
         dialogId: dialogId,
         title: title,
         message: message,
         cancel: cancel,
         confirm: confirm
      }).catch(() => false).then((answer) => {
         if (!answer) {
            formElement.dispatchEvent(new CustomEvent(CONFIRM_DECLINED_EVENT));
         } else if (pending && pending.state !== "initialized") {
            Confirm.resubmit(formElement, submitter);
         }

         return answer;
      });
   }

   // Turbo's own submit path for the form, answered already: it builds a fresh submission that
   // skips the confirm and sends the request.
   private static resubmit(formElement, submitter) {
      answeredForm = formElement;

      try {
         pageNavigator.submitForm(formElement, submitter ?? undefined);
      } finally {
         answeredForm = null;
      }
   }

   static askConfirmation(options) {
      options = {
         ...{
            dialogId: "confirm-dialog",
            title: null,
            message: null,
            cancel: null,
            confirm: null
         },
         ...removeEmpty(options)
      };

      const confirmDialogElement = document.getElementById(options.dialogId);

      if (!confirmDialogElement) {
         throw new Error(`The confirm dialog with id '${options.dialogId}' was not found.`);
      }

      if (options.title) {
         const titleElement = confirmDialogElement.querySelector("#confirm-dialog-title");
         titleElement.innerText = options.title;
      }

      if (options.message) {
         const messageElement = confirmDialogElement.querySelector("#confirm-dialog-message");
         messageElement.innerText = options.message;
      }

      if (options.cancel) {
         const cancelButtonElement = confirmDialogElement.querySelector("#confirm-dialog-cancel-button");
         cancelButtonElement.innerText = options.cancel;
      }

      if (options.confirm) {
         const confirmButtonElement = confirmDialogElement.querySelector("#confirm-dialog-confirm-button");
         confirmButtonElement.innerText = options.confirm;
      }

      // Escape closes a dialog without setting returnValue, so an earlier answer would otherwise outlive it.
      confirmDialogElement.returnValue = "";
      confirmDialogElement.showModal();

      return new Promise((resolve, reject) => {
         confirmDialogElement.addEventListener(
            "close",
            (event) => {
               if (event.target.returnValue === "confirm") {
                  resolve(true);
               } else if (event.target.returnValue === "deny") {
                  resolve(false);
               } else {
                  reject();
               }
            },
            {
               once: true
            }
         );
      });
   }
}